import { useEffect, useMemo, useState } from "react";
import { Button, Input, Table } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { useRecoilValue } from "recoil";
import { brandKitsState, locationsState } from "../../../../state/atoms";
import { BrandKitDto, LocationDto } from "../../../../generated/dashboard-client";
import { env } from "../../../../env/env";
import { AssignKitToLocationModal } from "./AssignKitToLocationModal";
import "./LocationAssignmentsTab.scss";

const PAGE_SIZE = 10;

type StatusFilter = "all" | "assigned" | "unassigned";

interface Row {
  location: LocationDto;
  kit?: BrandKitDto;
}

export function buildKitByLocation(brandKits: BrandKitDto[]): Map<number, BrandKitDto> {
  const map = new Map<number, BrandKitDto>();
  brandKits.forEach((kit) => (kit.locationIds || []).forEach((id) => map.set(id, kit)));
  return map;
}

function KitCell({ kit }: { kit: BrandKitDto }) {
  const logoPath = (kit as any).firstLogoPath as string | undefined;
  const logoUrl = logoPath ? `${env.s3Path}/${logoPath}` : null;
  const label = kit.name || "Untitled";
  const initial = label.trim().charAt(0).toUpperCase() || "?";
  return (
    <span className="location-kit-cell">
      {logoUrl ? (
        <img className="location-kit-avatar" src={logoUrl} alt={label} />
      ) : (
        <span className="location-kit-avatar placeholder">{initial}</span>
      )}
      <span className="location-kit-name">{label}</span>
    </span>
  );
}

export function LocationAssignmentsTab() {
  const locations = useRecoilValue(locationsState);
  const brandKits = useRecoilValue(brandKitsState);

  const [query, setQuery] = useState("");
  const [status, setStatus] = useState<StatusFilter>("all");
  const [page, setPage] = useState(1);
  const [sortOrder, setSortOrder] = useState<"ascend" | "descend" | null>(null);

  const [assignLocation, setAssignLocation] = useState<LocationDto | null>(null);
  const [assignCurrentKit, setAssignCurrentKit] = useState<BrandKitDto | null>(null);
  const [assignOpen, setAssignOpen] = useState(false);

  const kitByLocation = useMemo(() => buildKitByLocation(brandKits), [brandKits]);

  const rows: Row[] = useMemo(
    () =>
      locations.map((location) => ({
        location,
        kit: location.internalId != null ? kitByLocation.get(location.internalId) : undefined,
      })),
    [locations, kitByLocation]
  );

  const assignedCount = useMemo(() => rows.filter((r) => r.kit).length, [rows]);
  const unassignedCount = rows.length - assignedCount;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      if (q && !(r.location.name || "").toLowerCase().includes(q)) return false;
      if (status === "assigned" && !r.kit) return false;
      if (status === "unassigned" && r.kit) return false;
      return true;
    });
  }, [rows, query, status]);

  useEffect(() => {
    setPage(1);
  }, [query, status]);

  const sorted = useMemo(() => {
    if (!sortOrder) return filtered;
    const s = [...filtered].sort((a, b) =>
      (a.location.name || "").localeCompare(b.location.name || "", undefined, { sensitivity: "base" })
    );
    return sortOrder === "descend" ? s.reverse() : s;
  }, [filtered, sortOrder]);

  const pageItems = useMemo(
    () => sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [sorted, page]
  );

  const openAssign = (row: Row) => {
    setAssignLocation(row.location);
    setAssignCurrentKit(row.kit || null);
    setAssignOpen(true);
  };

  const columns = [
    {
      title: "Location",
      key: "location",
      sorter: true,
      sortOrder: sortOrder,
      showSorterTooltip: false,
      render: (_: unknown, row: Row) => {
        const parts = [row.location.city, row.location.state].filter(Boolean);
        return (
          <div className="location-cell">
            <div className="location-name">{row.location.name}</div>
            {parts.length > 0 && <div className="location-address">{parts.join(", ")}</div>}
          </div>
        );
      },
    },
    {
      title: "Brand Kit",
      key: "kit",
      render: (_: unknown, row: Row) =>
        row.kit ? (
          <KitCell kit={row.kit} />
        ) : (
          <span className="location-no-kit">No Brand Kit assigned</span>
        ),
    },
    {
      title: (
        <div className="location-header-chips">
          <span className="location-chip assigned">{assignedCount} assigned</span>
          <span className="location-chip unassigned">{unassignedCount} unassigned</span>
        </div>
      ),
      key: "action",
      width: 220,
      align: "right" as const,
      render: (_: unknown, row: Row) => (
        <Button className="location-assign-btn" onClick={() => openAssign(row)}>
          {row.kit ? "Change" : "Assign"}
        </Button>
      ),
    },
  ];

  return (
    <div className="location-assignments-tab">
      <div className="location-assignments-toolbar">
        <Input
          className="location-assignments-search"
          size="large"
          placeholder="Search locations"
          prefix={<SearchOutlined style={{ color: "#999" }} />}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          allowClear
        />
        <div className="location-assignments-segment">
          {([
            { label: "All", value: "all" },
            { label: "Assigned", value: "assigned" },
            { label: "Unassigned", value: "unassigned" },
          ] as { label: string; value: StatusFilter }[]).map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={`location-segment-btn${status === opt.value ? " selected" : ""}`}
              onClick={() => setStatus(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <Table
        className="location-assignments-table"
        dataSource={pageItems}
        columns={columns}
        rowKey={(row) => String(row.location.internalId)}
        rowClassName={(row) => (row.kit ? "" : "location-row-unassigned")}
        pagination={
          filtered.length > PAGE_SIZE
            ? {
                current: page,
                pageSize: PAGE_SIZE,
                total: filtered.length,
                onChange: setPage,
                showSizeChanger: false,
              }
            : false
        }
        locale={{ emptyText: "No locations match your filters" }}
        onChange={(pagination, _filters, sorter) => {
          const s = Array.isArray(sorter) ? sorter[0] : sorter;
          setSortOrder((s?.order as "ascend" | "descend" | undefined) ?? null);
          if (pagination.current) setPage(pagination.current);
        }}
      />

      <AssignKitToLocationModal
        open={assignOpen}
        location={assignLocation}
        currentKit={assignCurrentKit}
        onClose={() => setAssignOpen(false)}
      />
    </div>
  );
}
